<template>
  <div class="chat-drawer-list pt-1">
    <v-switch
      class="v-input--reverse"
      color="accent"
      v-if="isAdmin"
      v-model="isArchived"
    >
      <template #label>
        <v-list-item-subtitle>
          Show archived only
        </v-list-item-subtitle>
      </template>
    </v-switch>
    <v-list>
      <v-list-item-group v-model="listItem" @change="itemHasChanged">
        <v-list-item
          v-for="chat in chatsChannels"
          :key="chat.id"
          :value="chat.id"
          three-line
          ripple="ripple"
        >
          <template>
            <v-list-item-avatar size="48" tile>
              <multi-avatar
                :users="getChatMembers(chat)"
                :current-user="currentUser"
              />
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title
                v-if="chat && chat.isDirectMessage && getDirectChatName(chat)"
              >
                {{ getDirectChatName(chat) }}
              </v-list-item-title>
              <v-list-item-title v-else>
                {{ chat.name }}
              </v-list-item-title>
              <v-list-item-subtitle class="role">
                {{ getChatSubtitle(chat) }}
              </v-list-item-subtitle>
              <v-list-item-subtitle>
                <div class="chat-drawer-list-subtitle-wrapper">
                  <div class="chat-drawer-list-subtitle">
                    <v-badge
                      :content="badgeValue(chat.id)"
                      :value="badgeValue(chat.id)"
                      :key="`${chat[titleKey]}-${badgeValue(chat.id)}`"
                      color="primary"
                      inline
                    >
                      <span
                        v-if="!isLastMessageDeleted(chat.id)"
                        :class="
                          draftMessageChannel(chat.id) &&
                            'chat-drawer-list-draft-message'
                        "
                      >
                        {{ getLastMessageText(chat.id) }}
                      </span>
                      <span
                        v-if="isLastMessageDeleted(chat.id)"
                        class="font-italic text--disabled"
                      >
                        Message removed
                      </span>
                    </v-badge>
                  </div>
                </div>
              </v-list-item-subtitle>
            </v-list-item-content>

            <div>
              <chat-more-menu
                v-if="isAdmin"
                :chat="chat"
                :archived="isChatHidden(chat)"
                @setChatArchive="$emit('setChatArchive', $event)"
              />
              <v-btn
                v-if="isChatPinned(chat)"
                small
                icon
                ripple="ripple"
                @click="pinClicked($event, chat)"
              >
                <v-img
                  max-width="16"
                  mam-height="16"
                  height="16"
                  width="16"
                  src="@/assets/chat-pin-active.svg"
                />
              </v-btn>

              <v-btn v-else small icon @click="pinClicked($event, chat)">
                <v-img
                  max-width="16"
                  mam-height="16"
                  height="16"
                  width="16"
                  src="@/assets/chat-pin.svg"
                />
              </v-btn>
            </div>

            <div class="v-list-item__after">&nbsp;</div>
          </template>
        </v-list-item>
      </v-list-item-group>
    </v-list>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import MultiAvatar from '@/components/avatars/MultiAvatar'
import { mapActions } from 'vuex'
import ChatMoreMenu from './chat/components/ChatMoreMenu.vue'
import { getDisplayName } from '@/helpers/getDisplayName'
export default {
  name: 'ChatDrawerList',
  components: {
    MultiAvatar,
    ChatMoreMenu,
  },
  props: {
    chats: {
      type: Array,
      default: () => {},
    },
    chatsUnreadedMessages: {
      type: Object,
      default: () => ({}),
    },
    titleKey: {
      type: String,
      default: 'name',
    },
    subtitleKey: {
      type: String,
      default: 'role',
    },
    lastChatsMessages: {
      type: Object,
      default: () => ({}),
    },
    currentChat: {
      type: [String, Number],
      default: '',
    },
    currentUser: {
      type: String,
      default: '',
    },
    roles: {
      type: Object,
      default: () => ({}),
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
    chatIdParam: {
      type: [String, Number],
      default: '',
    },
  },
  data() {
    return {
      listItem: undefined,
      isArchived: false,
    }
  },
  computed: {
    chatsChannels() {
      return this.chats.filter(
        chat =>
          !!chat.chatMembers.find(
            member =>
              member.uuid === this.currentUser &&
              member.isHidden === this.isArchived,
          ),
      )
    },
    ...mapState('profilePreviewsStates', ['profilePreviews']),
  },
  watch: {
    currentChat(val) {
      // need this watch to enter to new created chat
      if (val) {
        this.listItem = val
      }
    },
    chatIdParam(val) {
      if (val) {
        this.listItem = val
      }
    },
  },
  mounted() {
    if (this.chatIdParam) {
      this.$emit('itemSelected', this.chatIdParam || '')
    }
  },
  methods: {
    badgeValue(channel) {
      return this.chatsUnreadedMessages[channel] || ''
    },
    getLastMessage(channel) {
      return this.lastChatsMessages[channel] || {}
    },
    draftMessageChannel(channel) {
      let draftMessage = localStorage.getItem('draftMessage')
      if (draftMessage === 'undefined') draftMessage = null
      const draftMessageParse = JSON.parse(draftMessage)
      const draftMessageChannel = draftMessageParse?.find(
        el => el?.currentChat === channel,
      )
      return draftMessageChannel
    },
    getLastMessageText(channel) {
      const lastMessage = this.getLastMessage(channel)
      const draftMessageChannel = this.draftMessageChannel(channel)

      if (draftMessageChannel) {
        const text = 'Message draft' || ''
        return text.substring(0, 20)
      } else {
        if (lastMessage) {
          const text = lastMessage.text || ''
          // @todo set correct string length
          return text.substring(0, 20)
        }
      }

      return ''
    },
    isLastMessageDeleted(channel) {
      const lastMessage = this.getLastMessage(channel)
      if (lastMessage) {
        return !!lastMessage.deleted
      }

      return false
    },
    itemHasChanged(val) {
      this.$emit('itemSelected', val || '')
    },
    isChatPinned(chat) {
      return (
        chat.chatMembers.find(member => member.uuid === this.currentUser) || {}
      ).isChatPinned
    },
    pinClicked(e, chat) {
      e.preventDefault()
      e.stopPropagation()
      this.$emit('toggleChannelIsPinned', chat)
    },
    isChatHidden(chat) {
      return !!(
        chat.chatMembers.find(member => member.uuid === this.currentUser) || {}
      )?.isHidden
    },
    getChatMembers(chat) {
      return chat.chatMembers.filter(member => member.isActive)
    },
    getDirectChatName(chat) {
      const directMessageReceiverMember = chat.chatMembers.find(
        member => member.uuid !== this.currentUser,
      )
      const profile = this.profilePreviews.find(
        profile =>
          (directMessageReceiverMember.uuid &&
            profile.uuid === directMessageReceiverMember.uuid) ||
          (directMessageReceiverMember.profileId &&
            profile.profileId === directMessageReceiverMember.profileId),
      )
      const member = {
        ...directMessageReceiverMember,
        ...profile,
      }
      const { fullName, nick, firstName, lastName } = member

      return getDisplayName(fullName, firstName, lastName, nick) || chat.name

      /**
       * @todo: sprawdzenie chat memberow po zadaniu Dawida: https://itsorce.atlassian.net/browse/MVP4BH-2669
       */
      // return chatMembersWithoutMe.find(
      //   member =>
      //     member.uuid !== this.currentUser.uuid &&
      //     this.chatProfiles.find(el => el.profileId === member.profileId),
      // )
    },
    getChatSubtitle(chat) {
      let chatMembersWithoutMe = chat.chatMembers.filter(
        member => member.uuid !== this.currentUser && member.isActive,
      )
      const roleIds = chatMembersWithoutMe.map(member => {
        const profile = this.profilePreviews.find(
          profile =>
            (member.uuid && profile.uuid === member.uuid) ||
            (member.profileId && profile.profileId === member.profileId),
        )
        return profile?.roleId || member?.roleId
      })
      // Get rolenames by roleId and return only unique values
      const rolenames = roleIds
        .map(roleId => this.roles[roleId] || 'Participant')
        .filter((value, index, array) => array.indexOf(value) === index)
      return rolenames.join(', ')
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';
@import '@/styles/mixins.scss';

.v-list {
  &__context-menu {
    &.v-sheet {
      &.v-sheet.v-list:not(.v-sheet--outlined) {
        box-shadow: none;
      }
    }
    .v-list-item__icon {
      margin-right: 12px !important;
      text-align: center;
      display: block;
      .v-icon {
        color: $bh-blue;
      }
    }
    .v-list-item__title {
      font-weight: bold;
      font-size: 1rem;
      color: $bh-blue;
    }
  }
}

.chat-drawer-list {
  padding-bottom: 0;

  &-draft-message {
    color: $bh-blue;
    white-space: nowrap;
  }

  .v-list {
    padding-left: 16px;
    &-item {
      min-height: 0;
      margin-bottom: 8px;
      padding-left: 0;
      &::before {
        background-color: rgba(255, 255, 255, 0.6);
        border-radius: 8px;
        left: 16px;
        z-index: 2;
        transition: none;
      }

      &:not(.v-list-item--active):not(.v-list-item--disabled) {
        color: $bh-blue !important;
      }

      &:not(.v-list-item--active):hover {
        z-index: 3;

        &::before {
          opacity: 0.3 !important;
        }
      }

      .v-ripple__container {
        border-radius: 8px;
        left: 16px;
      }

      .v-avatar {
        margin: 8px 0;
      }
      &--active {
        color: $bh-blue !important;

        &::before {
          opacity: 1 !important;
          left: 16px;
          background-color: $bh-white;
        }

        &::after {
          background-color: $bh-white;
        }

        .v-ripple__container {
          display: none !important;
        }

        .v-list-item__after {
          opacity: 1;
        }

        .v-list-item__icon {
          order: 2;
          width: 48px;
          height: 48px;
          margin: 0 !important;
          background-color: $bh-white;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
        }
      }

      &__after {
        position: absolute;
        right: 0;
        top: -24px;
        height: calc(100% + 48px);
        width: 24px;
        opacity: 0;
        background-color: $bh-white;
        // transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
        z-index: 1;

        &::before,
        &::after {
          content: ' ';
          position: absolute;
          right: 0;
          width: 24px;
          height: 24px;
          background-color: $bh-blue--4;
          border-radius: 0;
        }

        &::before {
          top: 0;
          border-bottom-right-radius: 24px;
        }

        &::after {
          bottom: 0;
          border-top-right-radius: 24px;
        }
      }

      &__content {
        align-self: center;
        padding: 0 8px;
        width: 100%;
        justify-content: space-between;

        .v-btn {
          .v-ripple__container {
            border-radius: 50% !important;
            left: 0 !important;
          }
        }
      }

      &__icon {
        width: 16px;
        margin: 0 12px 0 6px !important;
        height: 48px;
        text-align: center;
        z-index: 2;
        min-width: 16px;
      }

      &__title {
        font-weight: 700;
        line-height: 1;
        z-index: 2;
        margin-bottom: 0;
        text-overflow: ellipsis;
        max-width: 120px;
      }

      &__subtitle {
        z-index: 2;
        &.role {
          font-size: 0.642857rem;
          color: $bh-gray--2;
        }
        .v-badge {
          color: $bh-black;
          font-size: 1rem;
        }
      }

      &__top-wrapper {
        flex-direction: row;
        display: flex;
        justify-content: space-between;
      }

      &--hidden {
        display: none;
      }
    }
  }

  .v-input {
    &--reverse {
      margin: 0;
      .v-input__slot {
        flex-direction: row-reverse;
        justify-content: flex-end;
        padding: 0 20px;
        .v-application--is-ltr & {
          .v-input--selection-controls__input {
            margin-right: 0;
            margin-left: 8px;
          }
        }
        .v-application--is-rtl & {
          .v-input--selection-controls__input {
            margin-left: 0;
            margin-right: 8px;
          }
        }
      }
    }
  }

  &-subtitle-wrapper {
    display: flex;
  }

  &-subtitle {
    width: 100%;
  }
}
</style>
